import { useEffect, useRef, useState, type ReactNode } from 'react';

/** Card with optional header (title + sub + right-aligned actions). */
export function Card({ title, sub, actions, children, className = '', bodyClass = 'p-5', hover = false }: {
  title?: ReactNode; sub?: ReactNode; actions?: ReactNode; children: ReactNode; className?: string; bodyClass?: string; hover?: boolean;
}) {
  return (
    <section className={`bg-card border border-line rounded-xl shadow-sh ${hover ? 'transition-shadow hover:shadow-sh2' : ''} ${className}`}>
      {(title || actions) && (
        <header className="flex items-center justify-between gap-3 px-5 py-3.5 border-b border-line-2">
          <div className="min-w-0">
            {title && <h3 className="text-[13.5px] font-semibold text-navy tracking-[-0.1px] truncate">{title}</h3>}
            {sub && <div className="text-[11px] text-muted mt-0.5">{sub}</div>}
          </div>
          {actions && <div className="flex items-center gap-2 flex-none">{actions}</div>}
        </header>
      )}
      <div className={bodyClass}>{children}</div>
    </section>
  );
}

const ACCENT: Record<string, string> = {
  teal: 'bg-teal', green: 'bg-green', gold: 'bg-gold', purple: 'bg-purple', navy: 'bg-navy', red: 'bg-red', amber: 'bg-amber',
};

/** KPI / stat tile with an accent bar. */
export function StatTile({ label, value, note, accent = 'teal' }: { label: string; value: ReactNode; note?: ReactNode; accent?: string }) {
  return (
    <div className="relative overflow-hidden bg-card border border-line rounded-xl shadow-sh px-4 py-3.5">
      <span className={`absolute left-0 top-0 bottom-0 w-[3px] ${ACCENT[accent] ?? ACCENT.teal}`} />
      <div className="text-[10px] font-semibold uppercase tracking-[0.5px] text-muted">{label}</div>
      <div className="mt-1 text-[24px] font-extrabold text-navy leading-none tracking-[-0.6px] mono">{value}</div>
      {note && <div className="mt-1 text-[10.5px] text-muted-2">{note}</div>}
    </div>
  );
}

/** Page header: eyebrow + title + optional description and right-side actions. */
export function PageHeader({ eyebrow, title, desc, actions }: { eyebrow: string; title: string; desc?: string; actions?: ReactNode }) {
  return (
    <div className="flex justify-between items-end gap-4 flex-wrap mb-5">
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-[0.6px] text-muted-2">{eyebrow}</div>
        <h1 className="text-[22px] text-navy tracking-[-0.4px] mt-0.5">{title}</h1>
        {desc && <p className="text-[12.5px] text-muted mt-1">{desc}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

const PILL: Record<string, string> = {
  green: 'bg-green-50 text-green', amber: 'bg-amber-50 text-amber', red: 'bg-red-50 text-red',
  teal: 'bg-teal-50 text-teal-d', navy: 'bg-navy-50 text-navy', gray: 'bg-[#eef1f5] text-muted',
  purple: 'bg-purple-50 text-purple', gold: 'bg-gold-50 text-gold',
};
export function Pill({ tone = 'gray', children, className = '' }: { tone?: string; children: ReactNode; className?: string }) {
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-bold ${PILL[tone] ?? PILL.gray} ${className}`}>{children}</span>;
}

/**
 * "Coming soon" banner for sections that aren't built/sourced yet, so the product reads as
 * phased (Phase 1 done, Phase 2 coming) rather than incomplete. Founder demo, 3 Aug 2026.
 */
export function ComingSoon({ title = 'Coming soon', note }: { title?: string; note?: ReactNode }) {
  return (
    <div className="rounded-card border border-dashed border-line bg-canvas px-5 py-6 text-center">
      <div className="text-[12px] font-bold uppercase tracking-[0.5px] text-teal-d">🚧 {title}</div>
      {note && <p className="text-[12.5px] text-muted mt-1.5 max-w-[520px] mx-auto leading-relaxed">{note}</p>}
    </div>
  );
}

/** Slim progress bar. */
export function ProgressBar({ pct, tone = 'teal' }: { pct: number; tone?: string }) {
  return (
    <div className="h-1.5 bg-line rounded-full overflow-hidden">
      <div className={`h-full rounded-full transition-[width] duration-500 ${ACCENT[tone] ?? ACCENT.teal}`} style={{ width: `${Math.max(0, Math.min(100, pct))}%` }} />
    </div>
  );
}

/* ─────────────────────────── Loading primitives ─────────────────────────── */

const dim = (x?: number | string) => (typeof x === 'number' ? `${x}px` : x);

/** Inline animated spinner (currentColor). */
export function Spinner({ size = 16, className = '' }: { size?: number; className?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={`animate-spin shrink-0 ${className}`} role="img" aria-label="Loading">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" className="opacity-20" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

/** Spinner + label meant to sit inside a (disabled) button while its action is in flight. */
export function InlineBusy({ children, size = 13, className = '' }: { children: ReactNode; size?: number; className?: string }) {
  return <span className={`inline-flex items-center gap-1.5 ${className}`}><Spinner size={size} />{children}</span>;
}

/** Pulsing placeholder block. `lines > 1` stacks several bars (last one shorter). */
export function Skeleton({ w, h = 12, lines = 1, className = '', rounded = 'rounded-md' }: {
  w?: number | string; h?: number | string; lines?: number; className?: string; rounded?: string;
}) {
  if (lines <= 1) {
    return <div aria-hidden="true" className={`animate-pulse bg-line-2 ${rounded} ${className}`} style={{ width: dim(w) ?? '100%', height: dim(h) }} />;
  }
  return (
    <div aria-hidden="true" className={`space-y-2 ${className}`}>
      {Array.from({ length: lines }).map((_, i) => (
        <div key={i} className={`animate-pulse bg-line-2 ${rounded}`} style={{ width: i === lines - 1 ? '70%' : (dim(w) ?? '100%'), height: dim(h) }} />
      ))}
    </div>
  );
}

const EXPECT_COPY = "This can take up to ~60s — you can keep working, this panel updates when it's ready.";
const STALL_COPY = 'Still working — this is taking longer than usual.';

/**
 * Animated multi-stage loader with a ticking elapsed-seconds counter.
 * Advances through `stages` on a timer but CAPS at the second-to-last stage —
 * it only reaches the final ("Done") stage once `settled` is true, so it can
 * never claim completion before the real work resolves.
 * Pass `error` + `onRetry` to show a failure state with a Retry button.
 */
export function StagedLoader({ stages, settled = false, current, error, onRetry, stageMs = 7000, note, className = '' }: {
  stages: string[]; settled?: boolean; current?: number | null; error?: string | null; onRetry?: () => void; stageMs?: number; note?: ReactNode; className?: string;
}) {
  const [idx, setIdx] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const startedAt = useRef(Date.now());
  // When `current` is provided the loader reflects REAL progress (each stage marked complete
  // as its request resolves); otherwise it advances on a timer (capped short of "Done").
  const driven = current != null;

  useEffect(() => {
    const t = setInterval(() => setElapsed(Math.floor((Date.now() - startedAt.current) / 1000)), 1000);
    return () => clearInterval(t);
  }, []);

  const cap = Math.max(0, stages.length - 2); // last "in-progress" stage index
  useEffect(() => {
    if (driven || error || settled || idx >= cap) return;
    const t = setTimeout(() => setIdx((i) => Math.min(i + 1, cap)), stageMs);
    return () => clearTimeout(t);
  }, [idx, cap, settled, error, stageMs, driven]);

  if (error) {
    return (
      <div role="alert" className={`rounded-lg border border-red/30 bg-red-50 px-4 py-3 ${className}`}>
        <div className="flex items-start gap-2.5">
          <span className="text-red text-[15px] leading-none mt-0.5">⚠</span>
          <div className="min-w-0 flex-1">
            <div className="text-[12.5px] font-semibold text-red">Something went wrong</div>
            <div className="text-[11.5px] text-red/90 mt-0.5 break-words">{error}</div>
          </div>
          {onRetry && (
            <button type="button" onClick={onRetry} className="flex-none px-2.5 py-1 rounded-md text-[11.5px] font-semibold bg-white text-navy border border-line hover:bg-canvas">↻ Retry</button>
          )}
        </div>
      </div>
    );
  }

  const shownIdx = settled ? stages.length - 1 : driven ? Math.min(Math.max(current!, 0), cap) : idx;
  const stalling = elapsed >= 45;
  const pct = stages.length ? ((shownIdx + (settled ? 1 : 0.5)) / stages.length) * 100 : 0;

  return (
    <div role="status" aria-busy="true" aria-live="polite" className={`rounded-lg border border-line-2 bg-canvas px-4 py-3 ${className}`}>
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <Spinner size={15} className="text-teal-d" />
          <span className="text-[12.5px] font-semibold text-navy truncate">{stages[shownIdx]}</span>
        </div>
        <span className="text-[11px] mono text-muted-2 tabular-nums flex-none">{elapsed}s</span>
      </div>
      <ol className="space-y-1 mb-2.5">
        {stages.map((s, i) => {
          const done = settled || i < shownIdx;
          const active = !settled && i === shownIdx;
          return (
            <li key={i} className="flex items-center gap-2 text-[11.5px]">
              <span className="flex-none w-3.5 flex justify-center">
                {done ? <span className="text-green text-[11px] leading-none">✓</span>
                  : active ? <Spinner size={11} className="text-teal-d" />
                    : <span className="w-1.5 h-1.5 rounded-full bg-line inline-block" />}
              </span>
              <span className={done ? 'text-muted' : active ? 'text-navy font-semibold' : 'text-muted-2'}>{s}</span>
            </li>
          );
        })}
      </ol>
      <ProgressBar pct={pct} />
      <p className={`text-[11px] mt-2 ${stalling ? 'text-amber font-semibold' : 'text-muted-2'}`}>{stalling ? STALL_COPY : (note ?? EXPECT_COPY)}</p>
    </div>
  );
}
